
import React from "react";
import { i18nManager } from "../../../../../i18n";
import { Schemes, Utils } from "../../../../../utils";
import { Elements } from "../../../../shared/Elements";
import * as ReactRouterDom from "react-router-dom";
import { DateHelper, DateTimeInput, DateTimePickerLayout, DateTimePickerSelectionMode, Dropdown, FormControl, Table, Column, StringHelper, useScreenDimension } from "@rivtn/noseur";
import { ApiResponseData, BaseService, SanitizedResponse, EarningService, ITournamentTransaction, TransactionStatus, TransactionType, ITournamentEdition, TournamentService } from "../../../../../services";

export interface TournamentTransactionsProps {
    tournamentEdition: ITournamentEdition;
}

function TournamentTransactions(props: TournamentTransactionsProps) {

    const { tournamentEdition } = props;
    const { width } = useScreenDimension();
    const tournamentService = TournamentService.getInstance();
    const labels = i18nManager.Labels.host.manage_tournament.finance;
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [transactions, setTransactions] = React.useState<ApiResponseData<ITournamentTransaction> | undefined>({});

    React.useEffect(() => {
        fetchTransactions();
    }, [searchParams]);

    return (<div className="earnings">
        <div className="header">
            <div className="queries">
                <Dropdown dontMatchTargetSize noIcon scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", style: { marginLeft: 0 }, isFieldset: true, label: labels.source.title, }}
                    selectedOptionIndex={(labels.source.options!.findIndex((p) => p?.value === (searchParams.get("type"))) ?? -1) + 1}
                    textInputProps={{ style: { paddingLeft: 15 } }}
                    options={[{ value: "", label: i18nManager.Labels.common.all }, ...labels.source.options!]} onSelectOption={(option) => {
                        Utils.updateSearchParams("type", option.value, setSearchParams, fetchTransactions);
                    }} />
                <Dropdown noIcon scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: labels.currency, }}
                    selectedOptionIndex={(i18nManager.CountriesAndCurrencies?.findIndex((p) => p?.currency_code === (searchParams.get("curr"))) ?? -1) + 1}
                    textInputProps={{ style: { paddingLeft: 15 } }}
                    options={[{ currency_code: "", currency_name: i18nManager.Labels.common.all }, ...(i18nManager.CountriesAndCurrencies ?? [])]} optionMap={{
                        value: "currency_code",
                        label: "{currency_name} ({currency_code})",
                    }} onSelectOption={(option) => {
                        Utils.updateSearchParams("curr", option.currency_code, setSearchParams, fetchTransactions);
                    }} />
                <FormControl className="date-range-dd" isFieldset scheme={Schemes.OMNEHOST} label={labels.date_range} rightContent={"fa fa-calendar"} style={{ width: 360 }}>
                    <DateTimeInput fill leftLayout={DateTimePickerLayout.FINE_LEFT_LAYOUT} formatDate={(date) => Utils.formatToFineDate4(date)}
                        selectedDates={[(searchParams.has("from") ? new Date(searchParams.get("from")!) : DateHelper.getStartOfMonth()), new Date(searchParams.get("to") ?? Utils.formatToApiDate(new Date(), false, false)!)]}
                        selectionMode={DateTimePickerSelectionMode.RANGE} onSelectDate={(options) => {
                            Utils.updateSearchParamses({
                                "to": Utils.formatToApiDate(options.toDate!, false, false),
                                "from": Utils.formatToApiDate(options.fromDate!, false, false),
                            }, setSearchParams, fetchTransactions);
                        }} />
                </FormControl>
            </div>
            <span className="link" onClick={downloadTransactions}>{labels.export}</span>
        </div>
        <div className="transactions" style={{ border: "unset", boxShadow: "unset", padding: "0px 10px" }}>
            <ReactRouterDom.NavLink to={`../finance`} style={{ margin: "15px 0px", width: "fit-content" }} >
                <i className="fa fa-arrow-left" style={{ marginRight: 10 }} />
                <span>{labels.all_trans}</span>
            </ReactRouterDom.NavLink>
            <Table stripedRows scheme={Schemes.OMNEHOST} data={transactions?.content} totalRecords={transactions?.total_elements} dataRefreshKeys={["external_id", "created_at"]}
                style={{ flex: 1, width: "100%" }} rowsPerPage={parseInt(searchParams.get("size") ?? "100")} loadingState={Elements.LOADING_STATE}
                emptyState={Elements.buildEmptyListView("fa fa-coins", labels.table.empty_label_parts.no_trans, { marginTop: 70 })}>
                <Column className="table-c" header={labels.table.headers.date} dataKey="created_at" template={(v) => Utils.formatToFineDate(v, true)} />
                <Column className="table-c" header={labels.table.headers.source} dataKey="source" template={(v: string) => Utils.toSaneSentenceFormat(v).split(" ").slice(1).join(" ").replaceAll("Currency Balance", "").trim()} />
                <Column className="table-c" header={labels.table.headers.description} dataKey="description" />
                <Column className="table-c" header={labels.table.headers.amount} template={(v: ITournamentTransaction) => {
                    const isWithdrawal = v.type === TransactionType.DEBIT;
                    const prefix = `${i18nManager.getCurrencySign(v.currency_code)} ${isWithdrawal ? "-" : ""}`;
                    return (<span style={isWithdrawal ? { color: "#FF7171", } : undefined}>{prefix}{StringHelper.toPresentableMoneyValue(v.amount)} ({prefix}{StringHelper.toPresentableMoneyValue(v.payable_amount)})</span>);
                }} />
                <Column className="table-c" header={labels.table.headers.platform_charge} template={(v: ITournamentTransaction) => {
                    const isWithdrawal = v.source === TransactionType.DEBIT;
                    return (<span style={isWithdrawal ? { color: "#FF7171", } : undefined}>{i18nManager.getCurrencySign(v.currency_code)} {isWithdrawal ? "-" : ""}{StringHelper.toPresentableMoneyValue(v.platform_amount)} ({v.extra_data?.platform_charge_percentage ?? 0} %)</span>);
                }} />
            </Table>
            <div className="pagination">
                {Elements.buildPaginator(transactions ?? {},
                    ({ currentPage }, size) => Utils.updateSearchParamses({ "page": currentPage, size }, setSearchParams, fetchTransactions),
                    parseInt(searchParams.get("page") ?? "1"), { margin: 15, marginBottom: 5 })}
                {(width > 800) && Elements.buildSorter(labels.table.sorts, (sort) => Utils.updateSearchParamses({ sort }, setSearchParams, fetchTransactions), labels.table.sorts[0].label)}
            </div>
        </div>
    </div>);

    function fetchTransactions() {
        setTransactions(undefined);
        tournamentService.queryTransactions(tournamentEdition.tournament.external_id, {
            sort: searchParams.get("sort") ?? "",
            type: searchParams.get("type") ?? "",
            page: searchParams.get("page") ?? "1",
            size: searchParams.get("size") ?? "100",
            status__eq: TransactionStatus.COMPLETED,
            currency_code: searchParams.get("curr") ?? "",
            created_at_to: searchParams.get("to") ?? Utils.formatToApiDate(new Date())!,
            created_at_from: searchParams.get("from") ?? Utils.formatToApiDate(DateHelper.getStartOfMonth())!,
        }).then(({ apiResponse }: SanitizedResponse<any>) => {
            if (searchParams.has("test")) {
                setTransactions({ content: [] });
                return;
            }
            setTransactions(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

    function downloadTransactions() {
        const rows = [[labels.table.headers.date, labels.table.headers.source, labels.table.headers.description, labels.table.headers.amount, labels.table.headers.platform_charge]]
            .concat((transactions?.content ?? []).map((v) => {
                const isWithdrawal = v.source === TransactionType.WITHDRAWAL;

                return ([
                    Utils.formatToFineDate(v.created_at, true),
                    Utils.toSaneSentenceFormat(v.source).split(" ").slice(1).join(" ").replaceAll("Currency Balance", "").trim(),
                    v.description ?? "",
                    `${isWithdrawal ? "-" : ""}${i18nManager.getCurrencySign(v.currency_code)} ${StringHelper.toPresentableMoneyValue(v.payable_amount)}`,
                    `${isWithdrawal ? "-" : ""}${i18nManager.getCurrencySign(v.currency_code)} ${StringHelper.toPresentableMoneyValue(v.platform_amount)} (${v.extra_data?.platform_charge_percentage ?? 0} %)`,
                ]);
            }));
        Utils.downloadBlob(rows.map(row => row.map(String)
            .map((v: any) => v.replaceAll('"', '""'))
            .map((v: any) => `"${v}"`)
            .join(",")
        ).join('\r\n'), `omnehost_${tournamentEdition.name.replaceAll(/[^a-zA-Z0-9]/g, "_")}_tournament_transactions_${searchParams.get("from") ?? "2025-02-01 01:00:00 GMT"}_${searchParams.get("to") ?? Utils.formatToApiDate(new Date())}`);
    }

}

export default TournamentTransactions;
